import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { ChevronDownIcon, ChevronRightIcon, AlertCircle, CheckCircle, Clock, XCircle } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FilingTransmission, FilingTransmissionHistory, TransmissionStatus } from "@/types/filings";

interface FilingTransmissionHistoryProps {
  filingId: number;
}

export const FilingTransmissionHistoryComponent: React.FC<FilingTransmissionHistoryProps> = ({
  filingId,
}) => {
  const [transmissionHistory, setTransmissionHistory] = useState<FilingTransmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedTransmissions, setExpandedTransmissions] = useState<Set<number>>(new Set());

  useEffect(() => {
    fetchTransmissionHistory();
  }, [filingId]);

  const fetchTransmissionHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await ApiService.request<{success: boolean, transmissions: FilingTransmission[]}>(
        EndpointKeys.GET_FILING_TRANSMISSIONS,
        {
          urlParams: { id: filingId },
        }
      );
      
      if (response.success) {
        setTransmissionHistory(response.transmissions || []);
      } else {
        setError("Failed to load transmission history");
      }
    } catch (err) {
      console.error("Error fetching transmission history:", err);
      setError("Failed to load transmission history");
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = (transmissionId: number) => {
    const newExpanded = new Set(expandedTransmissions);
    if (newExpanded.has(transmissionId)) {
      newExpanded.delete(transmissionId);
    } else {
      newExpanded.add(transmissionId);
    }
    setExpandedTransmissions(newExpanded);
  };

  const getStatusIcon = (status: TransmissionStatus) => {
    switch (status) {
      case "COMPLETED":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "FAILED":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "SUBMITTED":
        return <Clock className="h-4 w-4 text-blue-600" />;
      case "DRAFT":
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusBadgeVariant = (status: TransmissionStatus) => {
    switch (status) {
      case "COMPLETED":
        return "default";
      case "FAILED":
        return "destructive";
      case "SUBMITTED":
        return "secondary";
      case "DRAFT":
        return "outline";
      default:
        return "outline";
    }
  };

  const formatXmlResponse = (xmlString: string | undefined) => {
    if (!xmlString) return "No response data";

    try {
      // Basic XML formatting for display
      const formatted = xmlString
        .replace(/></g, ">\n<")
        .split("\n")
        .map((line, index) => {
          const indent = "  ".repeat(Math.max(0, (line.match(/</g) || []).length - (line.match(/\//g) || []).length));
          return `${indent}${line.trim()}`;
        })
        .join("\n");

      return formatted;
    } catch (error) {
      return xmlString; // Return original if formatting fails
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-sm text-muted-foreground">Loading transmission history...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-sm text-red-600">{error}</div>
      </div>
    );
  }

  if (transmissionHistory.length === 0) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-sm text-muted-foreground">No transmission history found</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {transmissionHistory.map((transmission) => {
        const isExpanded = expandedTransmissions.has(transmission.id);
        return (
          <Card key={transmission.id} className="border">
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 pb-3"
              onClick={() => toggleExpanded(transmission.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusIcon(transmission.status)}
                  <div>
                    <div className="font-medium">
                      Transmission #{transmission.id}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {format(new Date(transmission.submittedAt), "dd/MM/yyyy HH:mm:ss")}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={getStatusBadgeVariant(transmission.status)}>
                    {transmission.status}
                  </Badge>
                  {transmission.isTest && (
                    <Badge variant="outline" className="text-orange-600 border-orange-300">
                      TEST
                    </Badge>
                  )}
                  {transmission.manualSubmission && (
                    <Badge variant="outline" className="text-purple-600 border-purple-300">
                      MANUAL
                    </Badge>
                  )}
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </div>
              </div>
            </CardHeader>
            
            {isExpanded && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Basic Details */}
                  <div className="space-y-3">
                    <h4 className="font-medium text-sm">Transmission Details</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="font-medium text-muted-foreground">Status:</span>
                        <span className="ml-2">{transmission.status}</span>
                      </div>
                      {transmission.correlationId && (
                        <div>
                          <span className="font-medium text-muted-foreground">Correlation ID:</span>
                          <span className="ml-2 font-mono text-xs bg-gray-100 px-1 py-0.5 rounded">
                            {transmission.correlationId}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-muted-foreground">Submitted:</span>
                        <span className="ml-2">{format(new Date(transmission.submittedAt), "dd/MM/yyyy HH:mm:ss")}</span>
                      </div>
                      {transmission.completedAt && (
                        <div>
                          <span className="font-medium text-muted-foreground">Completed:</span>
                          <span className="ml-2">{format(new Date(transmission.completedAt), "dd/MM/yyyy HH:mm:ss")}</span>
                        </div>
                      )}
                      {transmission.acceptedTime && (
                        <div>
                          <span className="font-medium text-muted-foreground">Accepted by HMRC:</span>
                          <span className="ml-2">{format(new Date(transmission.acceptedTime), "dd/MM/yyyy HH:mm:ss")}</span>
                        </div>
                      )}
                      {transmission.submittedBy && (
                        <div>
                          <span className="font-medium text-muted-foreground">Submitted by:</span>
                          <span className="ml-2">{transmission.submittedBy}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Error Information */}
                  {(transmission.errorCode || transmission.errorMessage) && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm text-red-600">Error Information</h4>
                      <div className="space-y-2 text-sm">
                        {transmission.errorCode && (
                          <div>
                            <span className="font-medium text-muted-foreground">Error Code:</span>
                            <span className="ml-2 font-mono text-xs bg-red-100 text-red-700 px-1 py-0.5 rounded">
                              {transmission.errorCode}
                            </span>
                          </div>
                        )}
                        {transmission.errorMessage && (
                          <div>
                            <span className="font-medium text-muted-foreground">Error Message:</span>
                            <div className="mt-1 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs">
                              {transmission.errorMessage}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Success Information */}
                  {transmission.irMarkReceipt && (
                    <div className="space-y-3 md:col-span-2">
                      <h4 className="font-medium text-sm text-green-600">Success Information</h4>
                      <div>
                        <span className="font-medium text-muted-foreground">IRmark Receipt:</span>
                        <div className="mt-1 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-xs font-mono">
                          {transmission.irMarkReceipt}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* HMRC Response */}
                {transmission.hmrcResponse && (
                  <div className="mt-4">
                    <h4 className="font-medium text-sm mb-2">Full HMRC Response</h4>
                    <ScrollArea className="h-64 w-full border rounded-md">
                      <pre className="p-4 text-xs bg-gray-50 whitespace-pre-wrap font-mono">
                        {formatXmlResponse(transmission.hmrcResponse)}
                      </pre>
                    </ScrollArea>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}; 